import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import en from '@angular/common/locales/en';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Injector,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { NZ_ICONS, NzIconModule, provideNzIcons } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { catchError, first, lastValueFrom, map, of, timeout } from 'rxjs';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/services/auth.service';
import { icons } from './icons-provider';
import { BaseColorService } from './shared/services/base-color.service';
import { CountryService } from './shared/services/country.service';
import { shouldProbeSession } from './shared/utils/session-probe.util';
import { customIcons } from '../assets/icons/custom-icons';

registerLocaleData(en);

const ngZorroConfig: NzConfig = {
  message: { nzTop: 28 },
  // notification: { nzTop: 240 },
};

export function appInitializerFactory(injector: Injector) {
  return () => {
    let currentPath = '';
    let isPublicRoute = false;

    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const hashPath = window.location.hash?.replace(/^#/, '');
      currentPath = pathname && pathname !== '/' ? pathname : hashPath;

      isPublicRoute =
        currentPath?.startsWith('/login') ||
        currentPath === '/register' ||
        currentPath === '/forgot-password' ||
        currentPath === '/reset-password' ||
        currentPath === '/validate-account' ||
        currentPath === '/unauthorized';
    }

    // `/login` lazy-loads child auth routes, so accept any path under it as
    // "the login route".
    const isLoginRoute = currentPath?.startsWith('/login');

    const authService = injector.get(AuthService);

    if (isPublicRoute) {
      // On the login route, try to silently restore the session from the
      // refresh-token cookie. The cookie is http-only so it can't be read from
      // JS — we just attempt the refresh and let the server decide: if it
      // succeeds the user already has a valid session → send them straight to
      // /home. If it fails (no/expired cookie → 401) the error is swallowed
      // and they stay on the login page to sign in. Other public routes
      // (register/forgot/reset/validate/unauthorized) are left untouched so a
      // logged-in user following such a link isn't bounced away.
      if (isLoginRoute) {
        // One probe per window, however often this page is loaded — see shouldProbeSession.
        if (!shouldProbeSession()) {
          return;
        }

        return lastValueFrom(
          authService.refreshToken().pipe(
            first(),
            map((user) => {
              if (user && typeof window !== 'undefined') {
                window.location.assign('/home');
              }

              return user;
            }),
            catchError(() => of(null))
          )
        );
      }
      // Nothing to bootstrap for public routes.
      return;
    }

    const countryService = injector.get(CountryService);
    const baseColorService = injector.get(BaseColorService);

    // Block bootstrap ONLY on the session check — guards/routing need to know
    // whether there's a user before the app renders. The timeout keeps a cold
    // origin from blank-screening the app indefinitely.
    return lastValueFrom(
      authService.restoreSession().pipe(
        first(),
        timeout(8000),
        catchError(() => of(null))
      )
    ).then((user) => {
      if (user) {
        // Fire-and-forget: trigger the reference data so it's cached and ready
        // for forms (countries, baseColors). Each service stores its result in
        // a BehaviorSubject and the select inputs read it reactively (and
        // self-fetch when empty), so first paint never waits on these
        // origin round-trips.
        countryService.ensureCountriesLoaded().subscribe({ error: () => {} });
        baseColorService.getAllForSelect().subscribe({ error: () => {} });
      }
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideNzIcons(icons),
    provideNzConfig(ngZorroConfig),
    provideNzI18n(en_US),
    ...provideTranslateService({
      fallbackLang: 'en',
      lang: 'en',
    }),
    ...provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
      useHttpBackend: true,
    }),
    importProvidersFrom(FormsModule, ReactiveFormsModule, NzNotificationModule, NzModalModule),
    importProvidersFrom(NzIconModule),
    { provide: NZ_ICONS, useValue: [...customIcons] },
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [Injector],
      multi: true,
    },
    provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
  ],
};
